import { useQuery } from '@tanstack/react-query';
import { shopifyApi } from '@/lib/api/services/shopify';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { Order } from './useCommerce';

interface UseShopifyOrdersOptions {
    filters?: {
        page?: number;
        limit?: number;
        search?: string;
        status?: string;
    };
}

export function useShopifyOrders({ filters }: UseShopifyOrdersOptions = {}) {
    const { selectedAccountId } = useBusinessAccountContext();
    const page = filters?.page || 1;
    const limit = filters?.limit || 20;

    return useQuery({
        queryKey: ['shopify-orders', selectedAccountId, filters],
        queryFn: async () => {
            if (!selectedAccountId) return { orders: [], total: 0, page, limit };

            try {
                // Determine offset based on page and limit
                const offset = (page - 1) * limit;
                const response = await shopifyApi.getLocalOrders(selectedAccountId, limit, offset);

                if (!response.orders) return { orders: [], total: 0, page, limit };

                // Map Shopify raw orders to unified Order interface
                const mappedOrders: Order[] = response.orders.map((shopifyOrder: any) => {
                    let paymentStatus = 'pending';
                    if (shopifyOrder.financial_status === 'paid') paymentStatus = 'paid';
                    else if (shopifyOrder.financial_status === 'refunded') paymentStatus = 'refunded';

                    return {
                        id: shopifyOrder.id,
                        order_number: shopifyOrder.order_number,
                        source: 'shopify',
                        customer_name: shopifyOrder.shipping_address?.name || shopifyOrder.email || 'Shopify Customer',
                        customer_email: shopifyOrder.email,
                        customer_phone: shopifyOrder.phone,
                        total_amount: parseFloat(shopifyOrder.total_price),
                        currency: shopifyOrder.currency,
                        status: shopifyOrder.fulfillment_status || 'unfulfilled',
                        payment_status: paymentStatus,
                        created_at: shopifyOrder.created_at,
                        items_count: shopifyOrder.line_items?.length || 0,
                        source_id: shopifyOrder.id
                    };
                });

                return {
                    orders: mappedOrders,
                    total: response.total || mappedOrders.length, // Ensure backend returns total count or handle approximation
                    page,
                    limit
                };
            } catch (error) {
                return { orders: [], total: 0, page, limit };
            }
        },
        enabled: !!selectedAccountId,
    });
}
